import { FHWAClasses } from "@app/shared";
import { colorForLaneNumber } from "../../../helpers/laneLineHelpers";

export const lightFhwaClasses: FHWAClasses =
    FHWAClasses.Class1 | FHWAClasses.Class2 | FHWAClasses.Class3;
export const mediumFhwaClasses: FHWAClasses =
    FHWAClasses.Class4 | FHWAClasses.Class5 | FHWAClasses.Class6 | FHWAClasses.Class7;
export const heavyFhwaClasses: FHWAClasses =
    FHWAClasses.Class8 | FHWAClasses.Class9 | FHWAClasses.Class10 |
    FHWAClasses.Class11 | FHWAClasses.Class12 | FHWAClasses.Class13;

export const getLaneNumberColor = (laneNumber: number | null | undefined): string => {
    return colorForLaneNumber(laneNumber);
};

export const getIntersectionName = (streetNames: Map<number, string>): string => {
    const uniqueStreets = Array.from(new Set(Array.from(streetNames.values()).filter(Boolean)));
    if (uniqueStreets.length > 1) {
        return uniqueStreets.join(" & ");
    } else if (uniqueStreets.length === 1) {
        return uniqueStreets[0];
    }
    return "";
};

export const formatPeakHourLabel = (peakStartTime: Date | null, peakEndTime: Date | null): string => {
    if (!peakStartTime || !peakEndTime) return "N/A";
    const formatTime = (d: Date) => d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
    return `${formatTime(peakStartTime)} - ${formatTime(peakEndTime)}`;
};

export interface RollingPeakHourResult<T> {
    peakStartTime: Date | null;
    peakEndTime: Date | null;
    peakStartIndex: number;
    peakTotal: number;
    peakRows: T[];
    recordsPerHour: number;
}

export function calculateRollingPeakHourGeneric<T>(
    rows: T[],
    getRowTime: (row: T) => Date | null | undefined,
    getRowTotal: (row: T) => number
): RollingPeakHourResult<T> {
    const emptyResult: RollingPeakHourResult<T> = {
        peakStartTime: null,
        peakEndTime: null,
        peakStartIndex: -1,
        peakTotal: 0,
        peakRows: [],
        recordsPerHour: 4,
    };

    if (!rows || rows.length === 0) {
        return emptyResult;
    }

    const sortedRows = [...rows].sort((a, b) => {
        const timeA = getRowTime(a)?.getTime() ?? 0;
        const timeB = getRowTime(b)?.getTime() ?? 0;
        return timeA - timeB;
    });

    let intervalMs = 15 * 60 * 1000;
    if (sortedRows.length >= 2) {
        const time0 = getRowTime(sortedRows[0])?.getTime();
        const time1 = getRowTime(sortedRows[1])?.getTime();
        if (time0 !== undefined && time1 !== undefined) {
            intervalMs = time1 - time0;
        }
    }

    const recordsPerHour = Math.round((60 * 60 * 1000) / intervalMs);

    let maxTotal = 0;
    let peakStartIndex = -1;

    const rowTotals = sortedRows.map((row) => getRowTotal(row));

    for (let i = 0; i <= sortedRows.length - recordsPerHour; i++) {
        let windowTotal = 0;
        for (let j = 0; j < recordsPerHour; j++) {
            windowTotal += rowTotals[i + j];
        }

        if (windowTotal > maxTotal) {
            maxTotal = windowTotal;
            peakStartIndex = i;
        }
    }

    if (peakStartIndex < 0) {
        return emptyResult;
    }

    const peakRows = sortedRows.slice(peakStartIndex, peakStartIndex + recordsPerHour);

    const peakStartTime = getRowTime(peakRows[0]) ?? null;
    const lastRowTime = getRowTime(peakRows[peakRows.length - 1]);
    const peakEndTime = lastRowTime ? new Date(lastRowTime.getTime() + intervalMs) : null;

    return {
        peakStartTime,
        peakEndTime,
        peakStartIndex,
        peakTotal: maxTotal,
        peakRows,
        recordsPerHour,
    };
}
